import to from 'await-to-js'
import {
  fetchMiniCart,
  fetchCartItems,
  applyPromotionToCurrentOrder,
  removePromotionFromCurrentOrder,
  fetchAdjustmentFromCurrentOrder,
} from '@/components/Navigation/MiniCart/order-queries'
import Axios from '@/config/axios'
import { isEmpty } from '@/helpers/object'

export default {
  namespaced: true,
  state: {
    promotionAdjustment: {},
    dataIsInitialized: false,
    completedAt: null,
    containsSocks: false,
    cartItemIsLoading: false,
    adjustmentIsLoading: false,
    quickAdding: false,
    items: [],
    number: '',
    mergedItemIndex: null,
    promoCode: '',
    promoDiscount: 0,
  },
  mutations: {
    setState(state, payload) {
      for (const key in payload) {
        state[key] = payload[key]
      }
    },
    setDataInitialized(state, payload = true) {
      state.dataIsInitialized = payload
    },
    addCartItem(state, payload) {
      state.items.unshift(payload)
    },
    revertItemsOrder(state) {
      state.items.reverse()
    },
    setQuickAdding(state, payload) {
      state.quickAdding = payload
    },
    cartItemIsLoading(state, payload) {
      state.cartItemIsLoading = payload
    },
    adjustmentIsLoading(state, payload) {
      state.adjustmentIsLoading = payload
    },
    updateCartItemNumber(state, payload) {
      state.items[payload.index].quantity = payload.quantity
    },
    mergeCartItemNumber(state, payload) {
      state.mergedItemIndex = payload.index
      state.items[payload.index].quantity = state.items[payload.index].quantity + payload.increment
      setTimeout(() => (state.mergedItemIndex = null), On.TransitionTimes.slow)
    },
    setAdjustment(state, payload) {
      state.promotionAdjustment = payload
    },
  },
  actions: {
    fetchData({ commit }) {
      fetchMiniCart().then(({ currentOrder }) => {
        commit('setState', currentOrder)
        commit('revertItemsOrder')
        commit('setDataInitialized')
        const skus = currentOrder ? currentOrder.items.map(item => item.sku) : []
        On.DynamicYield.updateRecommendation(skus)
        window.dispatchEvent(new CustomEvent('on:order-ready'))
      })
    },

    addCartItems({ state, commit }, payload) {
      // Show loader for missing cart item only if request takes more than one second
      const loadingTimer = setTimeout(() => {
        commit('cartItemIsLoading', true)
      }, 1000)

      // Don't show empty cart if product is being added but takes a while to show up
      commit('setQuickAdding', true)

      fetchCartItems(payload.ids).then(variants => {
        clearTimeout(loadingTimer)
        commit('cartItemIsLoading', false)
        variants.forEach(variant => {
          variant.variantId = variant.id
          delete variant.id
          if (variant.quantity === undefined) variant.quantity = 1

          // If item already exists in cart merge it, else add as a new item
          const itemIndex = getItemIndex(state, {
            skuId: variant.variantId,
          })
          itemIndex === -1
            ? commit('addCartItem', variant)
            : commit('mergeCartItemNumber', {
              index: itemIndex,
              increment: 1,
            })
        })
        commit('setQuickAdding', false)
      })
    },

    updateCartItemNumber({ state, commit, dispatch }, payload) {
      commit('updateCartItemNumber', {
        index: getItemIndex(state, payload),
        quantity: payload.quantity,
      })
      if (!isEmpty(state.promotionAdjustment)) commit('adjustmentIsLoading', true)
      dispatch('updateServer', {
        skuId: payload.skuId,
        quantity: payload.increase,
        onDone: payload.onDone,
      }).then(() => {
        dispatch('fetchAdjustment')
        window.dispatchEvent(new CustomEvent('on:update-cart'))
      })
    },

    updateServer({ getters }, { skuId, quantity, onDone }) {
      return Axios.post(getters.ordersPopulateUrl, {
        variant_id: skuId,
        quantity,
      }).then(response => {
        onDone && onDone()
        return response
      })
    },

    redirectToCheckout({ rootState }) {
      return Axios.patch(`/${rootState.page.locale}/cart?checkout`, {})
        .then(function(response) {
          if (response.data.success) window.location.href = `/${rootState.page.locale}/checkout`
        })
        .catch(function(error) {
          // TODO: Show error message and remove log
          console.log('Errror', error)
        })
    },

    async applyPromoCode({ state, commit }, promoCode) {
      const [error, response] = await to(applyPromotionToCurrentOrder(promoCode))
      if (error) throw { type: 'error', text: 'error' }
      const data = response.applyPromotionToCurrentOrder
      if (data.messageType == 'success') {
        commit('setAdjustment', data.order.promotionAdjustment)
        if (data.order.items.length > state.items.length)
          commit('addCartItem', data.order.items.pop())
        return { type: data.messageType, text: data.messageText }
      } else {
        throw { type: data.messageType, text: data.messageText }
      }
    },

    clearPromoCode({ commit }, promoCode) {
      removePromotionFromCurrentOrder(promoCode).then(() => commit('setAdjustment', {}))
    },
    fetchAdjustment({ commit }) {
      fetchAdjustmentFromCurrentOrder().then(data => {
        commit('setAdjustment', data.currentOrder.promotionAdjustment)
        commit('adjustmentIsLoading', false)
      })
    },
  },

  getters: {
    cartItemIsLoading: state => state.cartItemIsLoading,
    adjustmentIsLoading: state => state.adjustmentIsLoading,
    quickAdding: state => state.quickAdding,
    dataIsInitialized: state => state.dataIsInitialized,
    items: state => state.items,
    itemsLength: state => state.items.length,
    itemsCount: state => state.items.reduce((sum, item) => sum + item.quantity, 0),
    itemTotal: state =>
      state.dataIsInitialized && state.items.length > 0
        ? state.items.map(i => i.price * i.quantity).reduce((sum, i) => sum + i)
        : 0,
    shippingTotal: (state, getters, rootState, rootGetters) =>
      getters.itemTotal > rootGetters['store/shippingThreshold'] || getters.itemsLength <= 0
        ? 0
        : rootGetters['store/shippingCost'],
    total: (state, getters) => {
      return (
        getters.itemTotal +
        getters.shippingTotal +
        parseFloat((getters.promotionAdjustment && getters.promotionAdjustment.amount) || 0)
      )
    },
    promoCode: state => state.promotionAdjustment && state.promotionAdjustment.code,
    promoDiscount: state => (state.promotionAdjustment && state.promotionAdjustment.amount) || 0,
    promotionAdjustment: state => state.promotionAdjustment,
    mergedItemIndex: state => state.mergedItemIndex,
    ordersPopulateUrl: (state, getters, rootState) => `/${rootState.page.locale}/orders/populate`,
  },
}

// Helper functions
function getItemIndex(state, payload) {
  return state.items.findIndex(item => {
    return Number(item.variantId) === payload.skuId
  })
}
